import React, { useEffect, useState } from 'react'
import { apiErrorMessage, getAutoPunchSettings, updateAutoPunchSettings } from '../../../services/access'
import type { AutoPunchSettings, AutoPunchSettingsPayload } from '../../../services/access'
import { EmptyState, ErrorBanner, LoadingState } from './AdminUiPrimitives'
import { useI18n } from '../../../i18n/I18nContext'

type AutoPunchSettingsPanelProps = {
  canManageSettings: boolean
}

const defaultSettings: AutoPunchSettings = {
  auto_punch_enabled: false,
  auto_punch_in_after_minutes: 10,
  auto_punch_out_after_minutes: 10,
  require_scheduled_shift: true,
  auto_punch_out_mode: 'automatic',
}

const normalizeSettings = (settings?: Partial<AutoPunchSettings> | null): AutoPunchSettings => ({
  auto_punch_enabled: Boolean(settings?.auto_punch_enabled),
  auto_punch_in_after_minutes: settings?.auto_punch_in_after_minutes ?? defaultSettings.auto_punch_in_after_minutes,
  auto_punch_out_after_minutes: settings?.auto_punch_out_after_minutes ?? defaultSettings.auto_punch_out_after_minutes,
  require_scheduled_shift: settings?.require_scheduled_shift ?? defaultSettings.require_scheduled_shift,
  auto_punch_out_mode: settings?.auto_punch_out_mode === 'supervisor_review' ? 'supervisor_review' : defaultSettings.auto_punch_out_mode,
})

export const AutoPunchSettingsPanel: React.FC<AutoPunchSettingsPanelProps> = ({ canManageSettings }) => {
  const { t } = useI18n()
  const [settings, setSettings] = useState<AutoPunchSettings>(defaultSettings)
  const [form, setForm] = useState<AutoPunchSettingsPayload>({
    autoPunchEnabled: false,
    autoPunchInAfterMinutes: 10,
    autoPunchOutAfterMinutes: 10,
    requireScheduledShift: true,
    autoPunchOutMode: 'automatic',
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = async () => {
    if (!canManageSettings) return
    setLoading(true)
    setError('')
    try {
      const next = normalizeSettings(await getAutoPunchSettings())
      setSettings(next)
      setForm({
        autoPunchEnabled: next.auto_punch_enabled,
        autoPunchInAfterMinutes: next.auto_punch_in_after_minutes,
        autoPunchOutAfterMinutes: next.auto_punch_out_after_minutes,
        requireScheduledShift: next.require_scheduled_shift,
        autoPunchOutMode: next.auto_punch_out_mode,
      })
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to load auto-punch settings.')))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [canManageSettings])

  const save = async () => {
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const next = await updateAutoPunchSettings(form)
      setSettings(next)
      setNotice(t('Auto-punch settings saved.'))
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to save auto-punch settings.')))
    } finally {
      setSaving(false)
    }
  }

  if (!canManageSettings) {
    return <EmptyState title={t('Auto punch unavailable')} description={t('Your role does not include tenant settings access.')} />
  }
  if (loading) return <LoadingState label={t('Loading auto-punch settings...')} />

  return (
    <article className="panel workspace-panel stack" data-testid="auto-punch-settings-panel">
      <div className="admin-panel-toolbar">
        <div className="stack" style={{ gap: '.25rem' }}>
          <h3 style={{ margin: 0 }}>{t('Auto punch-in / punch-out')}</h3>
          <p className="text-muted">
            {t('Automatically punch workers in or out based on geofence dwell time. Only fires for confirmed scheduled assignments.')}
          </p>
        </div>
        <span className={`status-pill ${settings.auto_punch_enabled ? 'status-pill--approved' : 'status-pill--draft'}`}>
          {settings.auto_punch_enabled ? t('Enabled') : t('Disabled')}
        </span>
      </div>

      <ErrorBanner message={error} />
      {notice ? <p className="helper">{notice}</p> : null}

      <div className="admin-form-grid">
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={form.autoPunchEnabled}
            onChange={(e) => setForm((f) => ({ ...f, autoPunchEnabled: e.target.checked }))}
          />
          <span>{t('Enable automatic geofence punch-in / punch-out')}</span>
        </label>

        <label>
          <span>{t('Punch in after (minutes on site)')}</span>
          <input
            className="input"
            type="number"
            min={1}
            max={120}
            value={form.autoPunchInAfterMinutes}
            onChange={(e) => setForm((f) => ({ ...f, autoPunchInAfterMinutes: Math.max(1, Math.min(120, Number(e.target.value) || 10)) }))}
            disabled={!form.autoPunchEnabled}
          />
        </label>

        <label>
          <span>{t('Punch out after (minutes off site)')}</span>
          <input
            className="input"
            type="number"
            min={1}
            max={120}
            value={form.autoPunchOutAfterMinutes}
            onChange={(e) => setForm((f) => ({ ...f, autoPunchOutAfterMinutes: Math.max(1, Math.min(120, Number(e.target.value) || 10)) }))}
            disabled={!form.autoPunchEnabled}
          />
        </label>

        <label>
          <span>{t('Punch-out mode')}</span>
          <select
            className="input"
            value={form.autoPunchOutMode}
            onChange={(e) => setForm((f) => ({ ...f, autoPunchOutMode: e.target.value as 'automatic' | 'supervisor_review' }))}
            disabled={!form.autoPunchEnabled}
          >
            <option value="automatic">{t('Automatic — punch out immediately')}</option>
            <option value="supervisor_review">{t('Supervisor review — create pending action')}</option>
          </select>
        </label>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={form.requireScheduledShift}
            onChange={(e) => setForm((f) => ({ ...f, requireScheduledShift: e.target.checked }))}
            disabled={!form.autoPunchEnabled}
          />
          <span>{t('Only auto-punch workers with a confirmed scheduled assignment')}</span>
        </label>
      </div>

      <div className="admin-panel-toolbar">
        <p className="helper">
          {t('GPS accuracy and freshness gates apply — stale or low-accuracy points will not trigger auto punch. Manual punch behavior is unaffected.')}
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? t('Saving...') : t('Save auto-punch settings')}
        </button>
      </div>
    </article>
  )
}
